'use client';

import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';
import { formatExpiryTime, FormatExpiryTimeOptions } from './formatExpiryTime';

export type ExpiryClockProps = {
  /** Label above the time, for example that the link stays valid until then.
   *  Customer-facing copy, so the caller owns it. */
  label: ReactNode;
  /** When the link stops working. Not `number`: see formatExpiryTime's doc comment for why an
   *  epoch value must be converted to a Date by the caller rather than passed as a bare number. */
  expiresAt: Date | string;
  /** Passed through to formatExpiryTime for a deterministic render in tests. */
  formatOptions?: FormatExpiryTimeOptions;
  'data-testid'?: string;
};

/**
 * Shows when a magic link (or similar time-boxed action) expires, on a magic-link landing
 * screen. Renders nothing at all when `expiresAt` cannot be parsed, so an unparseable value
 * omits the clock rather than showing "Invalid Date".
 *
 * Wrapped in withClarityTheme: its surface token (theme.palette.surface.neutralSubtleRaised) only
 * exists on the Clarity theme, and this component has no control over what theme a future
 * caller renders it under. Nesting withClarityTheme inside an already Clarity-themed screen is
 * idempotent (the HOC re-derives the same Clarity theme from the ambient light/dark mode), so
 * this costs nothing for today's callers and fixes the silent no-background render for any
 * caller outside a Clarity tree.
 */
function ExpiryClockComponent({
  label,
  expiresAt,
  formatOptions,
  'data-testid': dataTestId,
}: Readonly<ExpiryClockProps>): ReactElement | null {
  const time = formatExpiryTime(expiresAt, formatOptions);
  if (!time) {
    return null;
  }

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        alignItems: 'center',
        textAlign: 'center',
        gap: theme.semantic.spacing.xs,
        padding: theme.semantic.spacing.base,
        borderRadius: theme.semantic.borderRadius.lg,
        backgroundColor: theme.palette.surface.neutralSubtleRaised,
      })}
    >
      <Stack
        direction="row"
        sx={(theme: Theme) => ({ alignItems: 'center', gap: theme.semantic.spacing.xs })}
      >
        {/* The artboard specifies the PERMANENT primary ramp for this icon, so it holds the
            same purple in dark mode: a countdown rendered in the near-black body-text colour
            reads as disabled, the opposite of what a live countdown means. */}
        <HourglassTopIcon
          aria-hidden
          sx={(theme: Theme) => ({
            // TD-034: the artboard sizes this icon at 18px; the size scale steps 16 ('8xs'), 24
            // ('7xs') with nothing between, so 18px is unreachable from a token.
            fontSize: theme.semantic.size['8xs'],
            color: theme.palette.permanent.primary,
          })}
        />
        <Typography variant="body2" color="text.tertiary">
          {label}
        </Typography>
      </Stack>
      {/* h4 is the theme variant that resolves to 24px semibold (fontSizeXl, see
          packages/styles/themes/clarity/common.ts), not h6: h6 resolves to 16px. component="p"
          keeps that size and weight without MUI's default h4-to-<h4> element mapping: an expiry
          time is a value, not a heading. */}
      <Typography variant="h4" component="p">
        {time}
      </Typography>
    </Stack>
  );
}

export const ExpiryClock = withClarityTheme(ExpiryClockComponent);
